// [AUD-3.2] Tokens ya no se envían via header Authorization
// Los tokens viajan como HttpOnly cookies (credentials: 'include')

import { config } from '../config';
import { useAuthStore } from '@/shared/store/auth.store';
import { AUTH_ENDPOINTS } from '@/features/auth/api/endpoints';

const API_BASE_URL = config.apiUrl;

// Error lanzado cuando el fetch falla antes de recibir respuesta HTTP
// (red caida, CORS, archivo local bloqueado por el OS al leer FormData).
// Util para que los consumidores diferencien errores de transporte de
// respuestas HTTP con error del servidor.
export class NetworkError extends Error {
  readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  isFormData?: boolean;
  _isRetry?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  // If a refresh is already in flight, reuse it so concurrent 401s
  // don't each rotate the single-use refresh token.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) return false;

      const usuario = await response.json();
      const { setAuth } = useAuthStore.getState();
      setAuth(usuario);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function handleSessionExpired(): never {
  const { clearAuth } = useAuthStore.getState();
  clearAuth();
  window.location.href = '/login';
  throw new Error('Sesión expirada. Inicie sesión nuevamente.');
}

export async function logoutApi(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.LOGOUT}`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort: clear local state even if API call fails
  }
  useAuthStore.getState().clearAuth();
}

export async function apiClient<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, isFormData = false, _isRetry = false } = options;

  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  if (!isFormData) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  if (body) {
    fetchOptions.body = isFormData ? (body as FormData) : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
  } catch (err) {
    // fetch rechaza con TypeError para fallas de red, CORS o streams del
    // body que no pueden leerse (ej. archivo bloqueado por otro proceso).
    if (err instanceof TypeError) {
      throw new NetworkError(err.message, err);
    }
    throw err;
  }

  if (!response.ok) {
    const isAuthEndpoint = endpoint === AUTH_ENDPOINTS.LOGIN || endpoint === AUTH_ENDPOINTS.REFRESH;

    if (response.status === 401 && !isAuthEndpoint) {
      if (!_isRetry) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          return apiClient<T>(endpoint, { ...options, _isRetry: true });
        }
      }
      handleSessionExpired();
    }

    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message ?? 'An error occurred');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export function getApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}
